import React, { useState } from "react";
import Icon from "../../../components/AppIcon";
import Button from "../../../components/ui/Button";
import Select from "../../../components/ui/Select";
import Input from "../../../components/ui/Input";

const RoleAssignment = ({ employee, onUpdate }) => {
  const [isEditing, setIsEditing] = useState(false);
  const [selectedRole, setSelectedRole] = useState(employee?.role);
  const [rolePercentage, setRolePercentage] = useState(100);
  const [effectiveDate, setEffectiveDate] = useState("");

  const roleOptions = [
    { label: "Kitchen Staff", value: "Kitchen Staff" },
    { label: "Front of House", value: "Front of House" },
    { label: "Bar Staff", value: "Bar Staff" },
    { label: "Management", value: "Management" },
  ];

  const handleSaveRole = () => {
    const updatedEmployee = {
      ...employee,
      role: selectedRole,
      rolePercentage,
      roleEffectiveDate: effectiveDate,
    };
    onUpdate(updatedEmployee);
    setIsEditing(false);
  };

  const handleCancel = () => {
    setSelectedRole(employee?.role);
    setRolePercentage(100);
    setEffectiveDate("");
    setIsEditing(false);
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h3 className="text-lg font-semibold text-gray-900">Role Assignment</h3>
        {!isEditing ? (
          <Button
            variant="outline"
            size="sm"
            iconName="Edit"
            onClick={() => setIsEditing(true)}
          >
            Modify Role
          </Button>
        ) : (
          <div className="flex gap-2">
            <Button variant="ghost" size="sm" onClick={handleCancel}>
              Cancel
            </Button>
            <Button
              variant="default"
              size="sm"
              iconName="Save"
              onClick={handleSaveRole}
            >
              Save Changes
            </Button>
          </div>
        )}
      </div>

      {/* Current Role */}
      <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
        <div className="flex items-center gap-3 mb-2">
          <Icon name="Briefcase" size={20} color="var(--color-primary)" />
          <h4 className="font-semibold text-gray-900">Current Role</h4>
        </div>
        <p className="text-lg font-medium text-gray-900">{employee?.role}</p>
        <p className="text-sm text-gray-600 mt-1">100% allocation</p>
      </div>

      {isEditing && (
        <div className="space-y-4 p-4 bg-gray-50 rounded-lg">
          <h4 className="font-semibold text-gray-900">
            Update Role Assignment
          </h4>

          <Select
            label="New Role"
            options={roleOptions}
            value={selectedRole}
            onChange={setSelectedRole}
            required
          />

          <Input
            type="number"
            label="Role Percentage"
            description="Percentage of time allocated to this role (1-100%)"
            value={rolePercentage}
            onChange={(e) => setRolePercentage(Number(e?.target?.value))}
            min="1"
            max="100"
            required
          />

          <Input
            type="date"
            label="Effective Date"
            description="When should this role change take effect?"
            value={effectiveDate}
            onChange={(e) => setEffectiveDate(e?.target?.value)}
            required
          />

          <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-3">
            <div className="flex items-start gap-2">
              <Icon
                name="AlertCircle"
                size={16}
                color="var(--color-accent)"
                className="mt-0.5"
              />
              <div className="text-sm text-gray-700">
                <p className="font-medium">Role Change Impact</p>
                <p className="mt-1">
                  Changing roles will automatically recalculate TRONC
                  distribution percentages based on the active rule
                  configuration.
                </p>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Role History */}
      <div>
        <h4 className="text-md font-semibold text-gray-900 mb-4">
          Role History
        </h4>
        <div className="space-y-3">
          {[
            {
              role: "Front of House",
              startDate: "15/01/2024",
              endDate: "Current",
              percentage: 100,
            },
            {
              role: "Bar Staff",
              startDate: "10/08/2023",
              endDate: "14/01/2024",
              percentage: 100,
            },
          ]?.map((history, idx) => (
            <div
              key={idx}
              className="flex items-center justify-between p-3 bg-gray-50 rounded-lg border border-gray-200"
            >
              <div>
                <p className="text-sm font-medium text-gray-900">
                  {history?.role}
                </p>
                <p className="text-xs text-gray-500">
                  {history?.startDate} - {history?.endDate}
                </p>
              </div>
              <div className="text-right">
                <p className="text-sm font-semibold text-gray-900">
                  {history?.percentage}%
                </p>
                <p className="text-xs text-gray-500">Allocation</p>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Eligibility Impact */}
      <div className="bg-green-50 border border-green-200 rounded-lg p-4">
        <div className="flex items-start gap-3">
          <Icon
            name="CheckCircle"
            size={20}
            color="var(--color-success)"
            className="mt-0.5"
          />
          <div>
            <h4 className="font-semibold text-gray-900 mb-1">
              TRONC Eligibility Status
            </h4>
            <p className="text-sm text-gray-700">
              This employee is currently <strong>eligible</strong> for TRONC
              distribution based on their role and service hours.
            </p>
            <div className="mt-2 text-xs text-gray-600">
              <p>
                • Minimum hours requirement: 20h/week (Current:{" "}
                {employee?.serviceHours}h)
              </p>
              <p>• Probationary period: Completed</p>
              <p>• Role eligibility: Eligible</p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default RoleAssignment;
